import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import {
  deleteFlowSegmentationAssignment,
  listFlowSegmentationAssignments,
  listStepConnectionsByFlowRole,
} from "./whatsappSegmentationApi";
import { FLOW_ROLE_OPTIONS } from "./whatsappFlowCatalogs";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const ButtonAdd = styled(Button)({
  flexGrow: 0,
  borderRadius: "20px",
  gap: "10px",
  backgroundColor: "#161e55",
  color: "#ffff",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
});

const flowLabel = (flow) => {
  const name = flow?.name != null ? String(flow.name).trim() : "";
  return name !== "" ? name : flow?.uuid ?? "—";
};

const cellText = (value) =>
  value != null && String(value).trim() !== "" ? String(value) : "—";

const centeredSpinnerBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "calc(100vh - 120px)",
  width: "100%",
};

export default function FlujosWhatsappSegmentaciones() {
  const navigate = useNavigate();

  const [flowRole, setFlowRole] = useState("");
  const [flows, setFlows] = useState([]);
  const [stepConnectionUuid, setStepConnectionUuid] = useState("");
  const [assignments, setAssignments] = useState([]);

  const [loadingFlows, setLoadingFlows] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const fetchFlows = useCallback(async (role) => {
    setLoadingFlows(true);
    setError(null);
    try {
      const data = await listStepConnectionsByFlowRole(role);
      setFlows(data);
    } catch (e) {
      setError(e?.message || "No se pudo cargar la lista de flujos.");
      setFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  }, []);

  const fetchAssignments = useCallback(async (uuid) => {
    setLoadingAssignments(true);
    setError(null);
    try {
      const data = await listFlowSegmentationAssignments(uuid);
      setAssignments(data);
    } catch (e) {
      setError(e?.message || "No se pudieron cargar las asignaciones.");
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  }, []);

  useEffect(() => {
    if (!flowRole) {
      setFlows([]);
      setStepConnectionUuid("");
      setAssignments([]);
      return;
    }
    setStepConnectionUuid("");
    setAssignments([]);
    fetchFlows(flowRole);
  }, [flowRole, fetchFlows]);

  useEffect(() => {
    if (!stepConnectionUuid) {
      setAssignments([]);
      return;
    }
    fetchAssignments(stepConnectionUuid);
  }, [stepConnectionUuid, fetchAssignments]);

  const handleFlowRoleChange = (event) => {
    setFlowRole(event.target.value);
  };

  const handleFlowChange = (event) => {
    setStepConnectionUuid(event.target.value);
  };

  const handleDelete = async (idAsignacion) => {
    if (!stepConnectionUuid) return;
    if (
      !window.confirm(
        `¿Eliminar la asignación ${idAsignacion}? Esta acción no se puede deshacer.`
      )
    ) {
      return;
    }
    setDeletingId(idAsignacion);
    setError(null);
    try {
      await deleteFlowSegmentationAssignment(stepConnectionUuid, idAsignacion);
      await fetchAssignments(stepConnectionUuid);
    } catch (e) {
      setError(e?.message || "No se pudo eliminar la asignación.");
    } finally {
      setDeletingId(null);
    }
  };

  const showAssignmentsTable = Boolean(stepConnectionUuid) && !loadingAssignments;

  const flowSelectPlaceholder = !flowRole
    ? "Primero elige el tipo"
    : loadingFlows
      ? "Cargando flujos…"
      : "Seleccionar flujo";

  return (
    <React.Fragment>
      <CssBaseline />
      <Toolbar />
      <Box sx={{ width: "100%", margin: "18px 14px" }}>
        <Stack spacing={2}>
          <Item>.</Item>
          <Item></Item>
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Box
                mt={2}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <h1 style={{ margin: 0, color: "#161e55" }}>Segmentaciones WhatsApp</h1>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Elige el tipo de flujo, luego un flujo en el listado; debajo verás las
                    asignaciones de segmentación.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <ButtonAdd
                    variant="contained"
                    onClick={() =>
                      navigate("/homeAdmin/flujos-whatsapp/segmentaciones/nueva")
                    }
                  >
                    Crear asignación
                  </ButtonAdd>
                  <ButtonSecondary
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/homeAdmin/flujos-whatsapp")}
                  >
                    Volver
                  </ButtonSecondary>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {error ? (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ maxWidth: 720 }}>
            <FormControl fullWidth size="small" disabled={loadingFlows}>
              <InputLabel id="flow-role-label">Tipo de flujo</InputLabel>
              <Select
                labelId="flow-role-label"
                id="flow-role"
                label="Tipo de flujo"
                value={flowRole}
                onChange={handleFlowRoleChange}
              >
                <MenuItem value="">
                  <em>Seleccionar</em>
                </MenuItem>
                {FLOW_ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              fullWidth
              size="small"
              disabled={!flowRole || loadingFlows || (flowRole && !loadingFlows && flows.length === 0)}
            >
              <InputLabel id="flow-select-label">Flujo</InputLabel>
              <Select
                labelId="flow-select-label"
                id="flow-select"
                label="Flujo"
                value={stepConnectionUuid}
                onChange={handleFlowChange}
              >
                <MenuItem value="">
                  <em>{flowSelectPlaceholder}</em>
                </MenuItem>
                {!loadingFlows &&
                  flows.map((flow, index) => (
                    <MenuItem
                      key={flow.uuid || `flow-${index}`}
                      value={flow.uuid}
                    >
                      {flowLabel(flow)}
                    </MenuItem>
                  ))}
              </Select>
              {flowRole && loadingFlows ? (
                <FormHelperText>Cargando listado de flujos…</FormHelperText>
              ) : null}
              {flowRole && !loadingFlows && flows.length === 0 ? (
                <FormHelperText>No hay flujos registrados para este tipo.</FormHelperText>
              ) : null}
            </FormControl>
          </Stack>

          {stepConnectionUuid && loadingAssignments ? (
            <Box sx={centeredSpinnerBoxSx}>
              <CircularProgress sx={{ color: "#161e55" }} />
            </Box>
          ) : null}

          {showAssignmentsTable ? (
            <TableContainer component={Paper}>
              <Table
                sx={{ minWidth: 650 }}
                size="small"
                aria-label="Tabla de asignaciones de segmentación"
              >
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Id asignación
                    </TableCell>
                    <TableCell align="left" sx={{ fontWeight: 600 }}>
                      Asignaciones
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No hay asignaciones para este flujo.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignments.map((row) => (
                      <TableRow
                        key={String(row.idAsignacion)}
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell align="center">{row.idAsignacion}</TableCell>
                        <TableCell
                          align="left"
                          sx={{ verticalAlign: "top", wordBreak: "break-word" }}
                        >
                          {cellText(row.asignaciones)}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="center"
                            flexWrap="wrap"
                          >
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              sx={{
                                textTransform: "none",
                                color: "#161e55",
                                borderColor: "#161e55",
                              }}
                              variant="outlined"
                            >
                              Editar
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<DeleteIcon />}
                              disabled={deletingId === row.idAsignacion}
                              onClick={() => handleDelete(row.idAsignacion)}
                              sx={{ textTransform: "none" }}
                            >
                              {deletingId === row.idAsignacion ? "Eliminando…" : "Eliminar"}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Stack>
      </Box>
      <Toolbar />
    </React.Fragment>
  );
};
